// ModalPagoCliente.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./ModalPagoCliente.css";
import { authFetch } from "../utils/authFetch";

export default function ModalPagoCliente({
  cliente,
  costoPlan,
  onClose,
  onSubmit,
  submitting = false,
}) {
  const [pendientes, setPendientes] = useState([]);
  const [siguientes, setSiguientes] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [formaPago, setFormaPago] = useState("oficina");
  const [referencia, setReferencia] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorLoad, setErrorLoad] = useState(null);

  // Instalación
  const [instalacion, setInstalacion] = useState(null);

  // ✅ ya no habrá checkbox; esto se activa si eliges un chip
  const [pagarInstalacion, setPagarInstalacion] = useState(false);

  // "1".."6" o "liquidar"
  const [instalacionModo, setInstalacionModo] = useState("1");

  // ✅ ADEUDOS
  const [adeudos, setAdeudos] = useState([]);
  const [adeudosLoading, setAdeudosLoading] = useState(true);

  /**
   * adeudosPago:
   * [{ adeudo_id, modo: "1" | "liquidar" }]
   */
  const [adeudosPago, setAdeudosPago] = useState([]);

  const precio = Number(costoPlan || 0);

  const iniciales = (() => {
    const partes = `${cliente.nombre || ""} ${cliente.apellidos || ""}`.trim().split(" ");
    const a = partes[0]?.[0] || "";
    const b = partes[1]?.[0] || "";
    return (a + b).toUpperCase();
  })();

  useEffect(() => {
    async function cargarMeses() {
      try {
        const res = await authFetch(`/api/pagos/meses_cliente/${cliente.id}`);
        const data = await res.json();
        if (data.ok) {
          setPendientes(data.pendientes || []);
          setSiguientes(data.siguientes || []);
        } else {
          setErrorLoad(data.error);
        }
      } catch {
        setErrorLoad("Error conectando con el servidor");
      } finally {
        setLoading(false);
      }
    }

    async function cargarInstalacion() {
      try {
        const res = await authFetch(`/api/pagos/instalacion/cliente/${cliente.id}`);
        const data = await res.json();
        if (data.ok && data.existe) setInstalacion(data);
        else setInstalacion(null);
      } catch {
        setInstalacion(null);
      }
    }

    async function cargarAdeudos() {
      try {
        setAdeudosLoading(true);
        const res = await authFetch(`/api/adeudos/cliente/${cliente.id}`);
        const data = await res.json();
        if (data.ok) setAdeudos(data.adeudos || []);
        else setAdeudos([]);
      } catch {
        setAdeudos([]);
      } finally {
        setAdeudosLoading(false);
      }
    }

    // reset UI al abrir modal/cambiar cliente
    setSeleccionados([]);
    setFormaPago("oficina");
    setReferencia("");
    setErrorLoad(null);
    setLoading(true);

    // ✅ reset instalación (sin check: se paga solo si eliges chip)
    setPagarInstalacion(false);
    setInstalacionModo("1");

    // ✅ reset adeudos
    setAdeudos([]);
    setAdeudosLoading(true);
    setAdeudosPago([]);

    cargarMeses();
    cargarInstalacion();
    cargarAdeudos();
  }, [cliente.id]);

  // ====== meses internet ======
  const claveMes = (m, tipo) => `${tipo}-${m.mes}-${m.anio}`;
  const estaSeleccionado = (m, tipo) => seleccionados.some((x) => x.clave === claveMes(m, tipo));

  const toggleMes = (m, tipo) => {
    if (m.pagado) return;
    if (submitting) return;

    const clave = claveMes(m, tipo);
    const existe = seleccionados.find((x) => x.clave === clave);

    if (existe) {
      setSeleccionados((prev) => prev.filter((x) => x.clave !== clave));
    } else {
      setSeleccionados((prev) => [...prev, { clave, mes: m.mes, anio: m.anio, texto: m.texto, tipo }]);
    }
  };

  // ====== adeudos ======
  const isAdeudoSelected = (adeudo_id, modo) =>
    adeudosPago.some((x) => x.adeudo_id === adeudo_id && x.modo === modo);

  const toggleAdeudoPago = (adeudo, modo) => {
    if (submitting) return;
    const adeudo_id = Number(adeudo.id);

    setAdeudosPago((prev) => {
      const exists = prev.find((x) => x.adeudo_id === adeudo_id && x.modo === modo);

      if (exists) return prev.filter((x) => !(x.adeudo_id === adeudo_id && x.modo === modo));

      const cleaned = prev.filter((x) => x.adeudo_id !== adeudo_id);
      return [...cleaned, { adeudo_id, modo }];
    });
  };

  const subtotalInternet = precio * seleccionados.length;

  // ===== instalación =====
  const mensualInst = instalacion ? Number(instalacion.monto_mensual || 0) : 0;
  const restantesInst = instalacion ? Number(instalacion.meses_restantes || 0) : 0;

  const nInst = (() => {
    if (!pagarInstalacion || !instalacion) return 0;
    if (instalacionModo === "liquidar") return restantesInst;
    const n = parseInt(instalacionModo || "1", 10);
    if (Number.isNaN(n) || n < 1) return 1;
    return Math.min(n, restantesInst);
  })();

  const subtotalInstalacion = pagarInstalacion ? mensualInst * nInst : 0;

  // ✅ subtotal adeudos (según selección)
  const subtotalAdeudos = useMemo(() => {
    if (!adeudosPago.length) return 0;

    let sum = 0;
    for (const sel of adeudosPago) {
      const a = adeudos.find((x) => Number(x.id) === Number(sel.adeudo_id));
      if (!a) continue;

      const mensual = Number(a.monto_mensual || 0);
      const rest = Number(a.meses_restantes || 0);

      if (sel.modo === "liquidar") sum += mensual * rest;
      else sum += mensual;
    }
    return sum;
  }, [adeudosPago, adeudos]);

  const total = subtotalInternet + subtotalInstalacion + subtotalAdeudos;

  const handleGuardar = () => {
    if (submitting) return;

    // ✅ permite solo instalación o solo internet o solo adeudos o combinaciones
    if (seleccionados.length === 0 && !pagarInstalacion && adeudosPago.length === 0) {
      alert("Selecciona al menos un mes, instalación o un adeudo.");
      return;
    }

    if (formaPago !== "oficina" && referencia.trim() === "") {
      alert("Debes capturar referencia.");
      return;
    }

    const payload = {
      cliente_id: cliente.id,

      // internet
      meses: seleccionados.map((m) => ({ mes: m.mes, anio: m.anio })),

      // forma pago
      forma_pago: formaPago,
      referencia: formaPago === "oficina" ? null : referencia.trim(),

      // instalación (se paga si eligieron chips)
      pagar_instalacion: pagarInstalacion,
      instalacion_modo: pagarInstalacion ? instalacionModo : null,

      // adeudos
      adeudos_pago: adeudosPago,
    };

    onSubmit(payload);
  };

  // ===== UI helpers instalación (chips) =====
  const instalacionExistePendiente = instalacion && Number(instalacion.meses_restantes || 0) > 0;

  const isInstSelected = (modo) => pagarInstalacion && instalacionModo === modo;

  const toggleInstalacionPago = (modo) => {
    if (submitting) return;

    // si vuelves a picar el mismo, lo deselecciona
    if (pagarInstalacion && instalacionModo === modo) {
      setPagarInstalacion(false);
      setInstalacionModo("1");
      return;
    }

    setPagarInstalacion(true);
    setInstalacionModo(modo);
  };

  return (
    <div className="mp-overlay">
      <div className="mp-modal">
        {/* Header */}
        <div className="mp-header">
          <div className="mp-avatar">{iniciales}</div>

          <div className="mp-header-info">
            <div className="mp-nombre">
              {cliente.nombre} {cliente.apellidos}
            </div>
            <div className="mp-subinfo">
              <span>Id: {cliente.id}</span>
              <span>Plan: {cliente.plan}</span>
              <span>${precio.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="mp-divider" />

        {/* Body */}
        <div className={"mp-body " + (submitting ? "mp-disabled" : "")}>
          {loading ? (
            <p className="mp-info-text">Cargando…</p>
          ) : errorLoad ? (
            <p className="mp-error-text">{errorLoad}</p>
          ) : (
            <>
              {/* Pendientes */}
              <section className="mp-section">
                <div className="mp-section-title mp-atrasados">
                  <strong>Meses atrasados</strong>
                </div>

                <div className="mp-mes-grid">
                  {pendientes.length === 0 ? (
                    <p className="mp-info-text">No hay meses pendientes.</p>
                  ) : (
                    pendientes.map((m) => (
                      <button
                        key={`pend-${m.mes}-${m.anio}`}
                        className={
                          "mp-mes-card mp-mes-atrasado " +
                          (m.pagado ? "mp-mes-disabled" : "") +
                          (estaSeleccionado(m, "pendiente") ? " mp-mes-selected" : "")
                        }
                        onClick={() => toggleMes(m, "pendiente")}
                        disabled={m.pagado || submitting}
                      >
                        <span>{m.texto}</span>
                        {m.pagado && <small>(Pagado)</small>}
                      </button>
                    ))
                  )}
                </div>
              </section>

              {/* Futuros */}
              <section className="mp-section">
                <div className="mp-section-title mp-futuros">
                  <strong>Meses futuros</strong>
                </div>

                <div className="mp-mes-grid">
                  {siguientes.map((m) => (
                    <button
                      key={`sig-${m.mes}-${m.anio}`}
                      className={
                        "mp-mes-card mp-mes-futuro " +
                        (m.pagado ? "mp-mes-disabled" : "") +
                        (estaSeleccionado(m, "siguiente") ? " mp-mes-selected" : "")
                      }
                      onClick={() => toggleMes(m, "siguiente")}
                      disabled={m.pagado || submitting}
                    >
                      <span>{m.texto}</span>
                      {m.pagado && <small>(Pagado)</small>}
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* ✅ ADEUDOS (solo si hay) */}
          {!adeudosLoading && adeudos.length > 0 && (
            <section className="mp-section">
              <div className="mp-section-title mp-atrasados">
                <strong>Adeudos (selecciona qué pagar)</strong>
              </div>

              <div className="mp-mes-grid">
                {adeudos.map((a) => {
                  const rest = Number(a.meses_restantes || 0);
                  const mensual = Number(a.monto_mensual || 0);
                  const titulo = Number(a.concepto) === 1 ? "Módem extra" : "Cambio de domicilio";

                  return (
                    <div
                      key={a.id}
                      style={{
                        display: "grid",
                        gap: 8,
                        padding: 10,
                        borderRadius: 14,
                        background: "rgba(255,255,255,0.55)",
                        border: "1px solid rgba(15,23,42,0.06)",
                      }}
                    >
                      <div style={{ display: "grid", gap: 2 }}>
                        <strong>{titulo}</strong>
                        <small>
                          Mensual: ${mensual.toFixed(2)} · Restan: {rest}
                        </small>
                      </div>

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className={
                            "mp-mes-card mp-mes-futuro " +
                            (isAdeudoSelected(a.id, "1") ? " mp-mes-selected" : "")
                          }
                          onClick={() => toggleAdeudoPago(a, "1")}
                          disabled={submitting || rest <= 0}
                          style={{ minWidth: 160 }}
                        >
                          <span>Pagar 1 mensualidad</span>
                        </button>

                        <button
                          type="button"
                          className={
                            "mp-mes-card mp-mes-atrasado " +
                            (isAdeudoSelected(a.id, "liquidar") ? " mp-mes-selected" : "")
                          }
                          onClick={() => toggleAdeudoPago(a, "liquidar")}
                          disabled={submitting || rest <= 1}
                          style={{ minWidth: 160, opacity: rest <= 1 ? 0.5 : 1 }}
                          title={rest <= 1 ? "No hay más de 1 mes para liquidar" : ""}
                        >
                          <span>Liquidar adeudo</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ✅ INSTALACIÓN (como adeudos, SIN CHECK, solo si hay) */}
          {instalacionExistePendiente && (
            <section className="mp-section">
              <div className="mp-section-title mp-atrasados">
                <strong>Instalación (selecciona qué pagar)</strong>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 10,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.55)",
                  border: "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <div style={{ display: "grid", gap: 2 }}>
                  <strong>Instalación</strong>
                  <small>
                    Mensual: ${mensualInst.toFixed(2)} · Restan: {restantesInst}
                  </small>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {Array.from({ length: Math.min(restantesInst, 6) }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={
                        "mp-mes-card mp-mes-futuro " +
                        (isInstSelected(String(n)) ? " mp-mes-selected" : "")
                      }
                      onClick={() => toggleInstalacionPago(String(n))}
                      disabled={submitting}
                      style={{ minWidth: 160 }}
                    >
                      <span>Pagar {n}</span>
                    </button>
                  ))}

                  {restantesInst > 1 && (
                    <button
                      type="button"
                      className={
                        "mp-mes-card mp-mes-atrasado " +
                        (isInstSelected("liquidar") ? " mp-mes-selected" : "")
                      }
                      onClick={() => toggleInstalacionPago("liquidar")}
                      disabled={submitting}
                      style={{ minWidth: 160 }}
                    >
                      <span>Liquidar instalación</span>
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Forma pago */}
          <section className="mp-section mp-section-pago">
            <label className="mp-label">Forma de pago</label>
            <select
              className="mp-select"
              value={formaPago}
              onChange={(e) => setFormaPago(e.target.value)}
              disabled={submitting}
            >
              <option value="oficina">Oficina (Ticket)</option>
              <option value="bancomer">Bancomer</option>
              <option value="banamex">Banamex</option>
              <option value="otro">Otro</option>
            </select>

            {formaPago !== "oficina" && (
              <input
                className="mp-input"
                placeholder="Referencia"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                disabled={submitting}
              />
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="mp-footer">
          <div className="mp-total">
            <div className="mp-total-label">Total</div>

            <div className="mp-total-detail">Internet: ${subtotalInternet.toFixed(2)}</div>

            {subtotalAdeudos > 0 && (
              <div className="mp-total-detail">Adeudos: ${subtotalAdeudos.toFixed(2)}</div>
            )}

            {subtotalInstalacion > 0 && (
              <div className="mp-total-detail">Instalación: ${subtotalInstalacion.toFixed(2)}</div>
            )}

            <div className="mp-total-amount">${total.toFixed(2)}</div>

            {submitting && <div className="mp-wait-hint">Procesando…</div>}
          </div>

          <div className="mp-footer-actions">
            <button className="mp-btn mp-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>

            <button
              className="mp-btn mp-btn-primary"
              disabled={
                loading ||
                submitting ||
                (seleccionados.length === 0 && !pagarInstalacion && adeudosPago.length === 0)
              }
              onClick={handleGuardar}
            >
              {submitting ? (
                <span className="mp-btn-loading">
                  <span className="mp-spinner" />
                  Procesando…
                </span>
              ) : (
                "Aceptar"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

